/**
 * osc_synth — sends note events directly to SuperCollider via WebSocket → OSC bridge.
 * Ordinary routing shutdown lets release tails finish; Panic remains an immediate stop.
 *
 * Implements the shared voice interface used by midi_synth and sample_synth:
 *   makeHex(coords, cents, ...) → { noteOn(), noteOff(), retune(), aftertouch() }
 *
 * Routing:
 *   /s_new, note /n_set (bend, pressure, expressionY, gate) → layer server (57101–57104)
 *   /n_set expressionY (mod wheel)            → broadcast to all four servers, node 1
 *   /n_set vol (fader)                        → node 1 on specific layer server (57101–57104)
 *
 * A shared 128-slot nearest-note pool bounds active voice bookkeeping.
 * Each attack gets fresh, increasing SC node IDs per layer so releasing tails
 * are not confused with a replacement voice reusing the same pool slot.
 *
 * Requires the osc-bridge Node.js process to be running locally:
 *   node osc-bridge/index.js
 */

import { outputTimestamp } from "../midi/output-transaction.js";
import { silentOutputHex } from "../audio/output-lifecycle.js";
import { VoicePool } from "../polyphony/voice-pool-nearest";
import { formantPresetToOscArgs, pickRandomFormantPreset } from "./formant-table.js";
import { debugEnabled, debugLog, warnLog } from "../debug/logging.js";

const WS_URL_DEFAULT = "ws://localhost:8089";
const SC_DISPATCH_PORT = 57100;
const OSC_LAYER_PORTS = [57101, 57102, 57103, 57104];
const NODE_ID_BASES = [100000, 300000, 500000, 700000];
const MAX_NOTE_SLOTS = 128;
const SC_MIN_NOTE_HZ = 20;
const SC_MAX_NOTE_HZ = 16000;
const midiCcToScParam = (value) => 1 + value / 127;

const isPlayableScFrequency = (value) =>
  Number.isFinite(value) && value >= SC_MIN_NOTE_HZ && value <= SC_MAX_NOTE_HZ;

const isPlayableScPitch = (frequency, bend = 1) =>
  isPlayableScFrequency(frequency) &&
  Number.isFinite(bend) &&
  bend > 0 &&
  isPlayableScFrequency(frequency * bend);

const _nextLayerNodeIds = NODE_ID_BASES.map((base) => base);
const nextNodeId = (layerIndex) => {
  const next = _nextLayerNodeIds[layerIndex];
  _nextLayerNodeIds[layerIndex] += 1;
  return next;
};

const clampMidiValue = (value, fallback = 64) => {
  if (!Number.isFinite(value)) return fallback;
  return Math.max(1, Math.min(127, Math.round(value)));
};

const normalizeOffVelocity = (value, fallback = 64) => {
  if (!Number.isFinite(value)) return fallback;
  return value <= 1 ? clampMidiValue(value * 127, fallback) : clampMidiValue(value, fallback);
};

const releaseNode = (socket, port, nodeId, offVel, timestamp) => {
  debugLog("osc", "releaseNode", { port, nodeId, offVel });
  socket.send(
    "/n_set",
    [
      { type: "i", value: nodeId },
      { type: "s", value: "off_vel" },
      { type: "f", value: offVel },
      { type: "s", value: "gate" },
      { type: "i", value: 0 },
    ],
    port,
    timestamp,
  );
};

const freeTargetGroup = (socket, port, targetGroup) => {
  socket.send("/g_freeAll", [{ type: "i", value: targetGroup }], port);
};

const createOscJitterTracker = (socket) => {
  const enabled = debugEnabled("oscjitter");
  let seq = 0;
  let lastBrowserPerf = null;

  return (kind, voiceId = -1) => {
    if (!enabled) return;
    const now = performance.now();
    const delta = lastBrowserPerf == null ? 0 : now - lastBrowserPerf;
    lastBrowserPerf = now;
    seq += 1;
    socket.send(
      "/hex/jitter",
      [
        { type: "i", value: seq },
        { type: "s", value: kind },
        { type: "f", value: now },
        { type: "f", value: delta },
        { type: "i", value: voiceId },
      ],
      SC_DISPATCH_PORT,
    );
  };
};

/**
 * Shared WebSocket connection, lazily created and reused across all OscHex instances.
 * Reconnects automatically if the bridge is restarted.
 */
class OscSocket {
  constructor(url) {
    this._url = url;
    this._ws = null;
    this._queue = []; // messages buffered while connecting
    this._disposed = false;
    this._refCount = 0;
    this._reconnectTimer = null;
    this._pendingBundles = new Map();
    this._bundleFlushScheduled = false;
    this._connect();
  }

  retain() {
    if (!this._disposed) this._refCount += 1;
    return this;
  }

  release() {
    if (this._disposed) return;
    this._refCount = Math.max(0, this._refCount - 1);
    if (this._refCount === 0) this.shutdown();
  }

  clearQueue() {
    this._queue = [];
    this._pendingBundles.clear();
    this._bundleFlushScheduled = false;
  }

  shutdown() {
    if (this._disposed) return;
    this._disposed = true;
    this.clearQueue();
    if (this._reconnectTimer != null) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }
    const ws = this._ws;
    this._ws = null;
    if (ws && typeof ws.close === "function") {
      ws.onopen = null;
      ws.onclose = null;
      ws.onerror = null;
      ws.close();
    }
    if (_sockets.get(this._url) === this) {
      _sockets.delete(this._url);
    }
  }

  _connect() {
    if (this._disposed) return;
    const ws = new WebSocket(this._url);

    ws.onopen = () => {
      if (this._disposed) {
        ws.close?.();
        return;
      }
      debugLog("osc", "Connected to osc-bridge:", this._url);
      this._ws = ws;
      for (const msg of this._queue) ws.send(JSON.stringify(msg));
      this._queue = [];
    };

    ws.onclose = () => {
      this._ws = null;
      if (this._disposed) return;
      warnLog("[osc_synth] osc-bridge disconnected. Reconnecting in 2s...");
      this._reconnectTimer = setTimeout(() => {
        this._reconnectTimer = null;
        this._connect();
      }, 2000);
    };

    ws.onerror = (e) => {
      if (this._disposed) return;
      warnLog("[osc_synth] osc-bridge WebSocket error:", e.message ?? e);
    };

    if (this._disposed) {
      ws.onopen = null;
      ws.onclose = null;
      ws.onerror = null;
      ws.close?.();
      return;
    }
  }

  _sendSerialized(message) {
    if (this._ws && this._ws.readyState === WebSocket.OPEN) {
      this._ws.send(message);
    } else {
      // Buffer by voice lifetime, not a fixed packet count: dropping a release
      // while retaining its attack leaves a permanent voice at the receiver.
      const packet = JSON.parse(message);
      for (const msg of packet.messages ?? [packet]) {
        const entry = packet.messages ? { ...packet, messages: [msg] } : packet;
        const node = msg.args?.[msg.address === "/s_new" ? 1 : 0]?.value;
        const sameNode = (queued) => {
          const prior = queued.messages?.[0] ?? queued;
          return queued.port === entry.port &&
            prior.args?.[prior.address === "/s_new" ? 1 : 0]?.value === node;
        };
        const terminal = msg.address === "/n_free" || (msg.address === "/n_set" &&
          msg.args.some((arg, i) => arg.value === "gate" && msg.args[i + 1]?.value === 0));
        if (terminal) {
          const neverSent = this._queue.some((queued) => sameNode(queued) &&
            (queued.messages?.[0] ?? queued).address === "/s_new");
          this._queue = this._queue.filter((queued) => !sameNode(queued));
          if (neverSent) continue; // Entire note ended before connection opened.
        } else if (msg.address === "/n_set") {
          const parameters = JSON.stringify(msg.args.filter((_, i) => i % 2 === 1));
          this._queue = this._queue.filter((queued) => {
            const prior = queued.messages?.[0] ?? queued;
            return !sameNode(queued) || prior.address !== "/n_set" ||
              JSON.stringify(prior.args.filter((_, i) => i % 2 === 1)) !== parameters;
          });
        }
        this._queue.push(entry);
      }
    }
  }

  _flushBundles() {
    this._bundleFlushScheduled = false;
    if (this._disposed) {
      this._pendingBundles.clear();
      return;
    }
    for (const bundle of this._pendingBundles.values()) {
      this._sendSerialized(JSON.stringify(bundle));
    }
    this._pendingBundles.clear();
  }

  send(address, args, port = OSC_LAYER_PORTS[0], timestamp) {
    if (this._disposed) return;
    const at = Number(timestamp);
    if (Number.isFinite(at)) {
      const key = `${port}:${at}`;
      let bundle = this._pendingBundles.get(key);
      if (!bundle) {
        const timeOrigin = Number(globalThis.performance?.timeOrigin);
        const fallbackOrigin = Date.now() - (globalThis.performance?.now?.() ?? 0);
        bundle = {
          port,
          timetagUnixMs: (Number.isFinite(timeOrigin) ? timeOrigin : fallbackOrigin) + at,
          messages: [],
        };
        this._pendingBundles.set(key, bundle);
      }
      bundle.messages.push({ address, args });
      if (!this._bundleFlushScheduled) {
        this._bundleFlushScheduled = true;
        queueMicrotask(() => this._flushBundles());
      }
      return;
    }
    this._sendSerialized(JSON.stringify({ port, address, args }));
  }
}

// One shared socket per wsUrl.
const _sockets = new Map();
function getSocket(url) {
  let socket = _sockets.get(url);
  if (!socket || socket._disposed) {
    socket = new OscSocket(url);
    _sockets.set(url, socket);
  }
  return socket.retain();
}

const buildSNewArgs = (
  synthName,
  id,
  targetGroup,
  freq,
  bend,
  onVel,
  mod,
  filter,
  vol,
  quickRelease,
  quickReleaseTime,
  retriggerBuzzFormant,
) => {
  const args = [
    { type: "s", value: synthName },
    { type: "i", value: id },
    { type: "i", value: 1 }, // addAction: addToTail
    { type: "i", value: targetGroup },
    { type: "s", value: "freq" },
    { type: "f", value: freq },
    { type: "s", value: "on_vel" },
    { type: "f", value: onVel },
    { type: "s", value: "bend" },
    { type: "f", value: bend },
    { type: "s", value: "pressure" },
    { type: "f", value: filter - 1 },
    { type: "s", value: "expressionY" },
    { type: "f", value: mod - 1 },
    { type: "s", value: "vol" },
    { type: "f", value: vol },
    { type: "s", value: "quick_release" },
    { type: "f", value: synthName === "tone" ? 0 : quickRelease },
    { type: "s", value: "quick_release_time" },
    { type: "f", value: synthName === "tone" ? 0.1 : quickReleaseTime },
    { type: "s", value: "gate" },
    { type: "i", value: 1 },
  ];

  if (synthName === "formant") {
    args.push(...formantPresetToOscArgs(pickRandomFormantPreset()));
  }

  if (synthName === "string" || synthName === "formant") {
    args.push(
      { type: "s", value: "retrigger_mode" },
      { type: "i", value: retriggerBuzzFormant ? 1 : 0 },
    );
  }

  return args;
};

/**
 * Factory — mirrors the async signature of create_midi_synth / create_mpe_synth.
 */
export const create_osc_synth = async (
  wsUrl = WS_URL_DEFAULT,
  synthNames = ["pluck", "string", "formant", "tone"],
  volumes = [0.5, 0.5, 0.5, 0.5],
  quickRelease = 0,
  quickReleaseTime = 0.1,
  quickReleaseRasterOnly = false,
  fundamental = 440,
  _reference_degree = 0,
  _scale = [0],
  targetGroup = 1,
  performanceOptions = {},
) => {
  // Local scsynth implements this same transport contract; all articulation,
  // random formants and controller mappings remain shared with external OSC.
  const socket = performanceOptions.transport ?? getSocket(wsUrl);
  let shutdown = false;
  const sendJitter = createOscJitterTracker(socket);
  const _volumes = [...volumes];
  const _mod = { value: 1.0 };
  const _quickRelease = { value: Math.max(0, Math.min(1, quickRelease)) };
  socket.setTailPruning?.(_quickRelease.value);
  const _quickReleaseTime = { value: Math.max(0.001, Math.min(2.5, quickReleaseTime)) };
  const _quickReleaseRasterOnly = { value: quickReleaseRasterOnly === true };
  const _retriggerBuzzFormant = { value: performanceOptions.retriggerBuzzFormant === true };
  const _pool = new VoicePool(Array.from({ length: MAX_NOTE_SLOTS }, (_, i) => i));
  const _knownNodeIds = new Set();
  // Logical held notes survive layer muting; tails and stolen voices do not.
  const _heldVoices = new Map();
  const _slotState = synthNames.map(() =>
    Array.from({ length: MAX_NOTE_SLOTS }, () => ({
      token: 0,
      active: false,
      onVel: 64,
      nodeId: null,
      quickReleaseEnabled: false,
    })),
  );

  const slotQuickReleaseValue = (slot) =>
    _quickReleaseRasterOnly.value
      ? slot?.quickReleaseEnabled
        ? _quickRelease.value
        : 0
      : _quickRelease.value;

  const setLayerVolume = (index, value) => {
    if (index < 0 || index >= _volumes.length) return;
    const next = Math.max(0, Math.min(1, value));
    const previous = _volumes[index];
    _volumes[index] = next;
    const layerState = _slotState[index];
    if (next === 0) {
      const now = performance.now();
      for (const slot of layerState) {
        if (slot.active && slot.nodeId != null) {
          const timestamp = slot.attackTimestamp != null && slot.attackTimestamp >= now
            ? slot.attackTimestamp + 1 : now;
          // Keep the current gain during release. SynthDefs free themselves
          // when their release envelope ends (Done.freeSelf).
          releaseNode(socket, OSC_LAYER_PORTS[index], slot.nodeId, 64, timestamp);
        }
        _knownNodeIds.delete(slot.nodeId);
        slot.active = false;
        slot.nodeId = null;
        slot.token += 1;
      }
      for (const voice of _heldVoices.values()) voice._nodeIds[index] = null;
      // Node 1 may be a group: setting its volume to zero would also silence
      // the tails we just released. Future attacks carry their own volume.
      return;
    }
    if (previous === 0 && next > 0 && !shutdown) {
      for (const voice of _heldVoices.values()) voice._startLayer(index);
    }
    for (const slot of layerState) {
      if (!slot?.active || slot.nodeId == null) continue;
      socket.send(
        "/n_set",
        [
          { type: "i", value: slot.nodeId },
          { type: "s", value: "vol" },
          { type: "f", value: next },
        ],
        OSC_LAYER_PORTS[index],
      );
    }
    // Keep node 1 in sync as the default for future note-ons / server-side
    // layer state even when nothing is currently sounding.
    socket.send(
      "/n_set",
      [
        { type: "i", value: 1 },
        { type: "s", value: "vol" },
        { type: "f", value: next },
      ],
      OSC_LAYER_PORTS[index],
    );
  };

  const setQuickRelease = (value) => {
    const next = Math.max(0, Math.min(1, value));
    _quickRelease.value = next;
    socket.setTailPruning?.(next);
    for (let i = 0; i < _slotState.length; i++) {
      if (synthNames[i] === "tone") continue; // Saw keeps its natural release.
      for (const slot of _slotState[i]) {
        if (!slot?.active || slot.nodeId == null) continue;
        socket.send(
          "/n_set",
          [
            { type: "i", value: slot.nodeId },
            { type: "s", value: "quick_release" },
            { type: "f", value: slotQuickReleaseValue(slot) },
          ],
          OSC_LAYER_PORTS[i],
        );
      }
    }
  };

  const setQuickReleaseTime = (value) => {
    const next = Math.max(0.001, Math.min(2.5, value));
    _quickReleaseTime.value = next;
    for (let i = 0; i < _slotState.length; i++) {
      if (synthNames[i] === "tone") continue;
      for (const slot of _slotState[i]) {
        if (!slot?.active || slot.nodeId == null) continue;
        socket.send(
          "/n_set",
          [
            { type: "i", value: slot.nodeId },
            { type: "s", value: "quick_release_time" },
            { type: "f", value: next },
          ],
          OSC_LAYER_PORTS[i],
        );
      }
    }
  };

  const setQuickReleaseRasterOnly = (value) => {
    _quickReleaseRasterOnly.value = value === true;
    for (let i = 0; i < _slotState.length; i++) {
      if (synthNames[i] === "tone") continue;
      for (const slot of _slotState[i]) {
        if (!slot?.active || slot.nodeId == null) continue;
        socket.send(
          "/n_set",
          [
            { type: "i", value: slot.nodeId },
            { type: "s", value: "quick_release" },
            { type: "f", value: slotQuickReleaseValue(slot) },
          ],
          OSC_LAYER_PORTS[i],
        );
      }
    }
  };

  const setBuzzFormantMode = (parameter, ref, value) => {
    ref.value = value === true;
    for (let i = 0; i < _slotState.length; i++) {
      if (synthNames[i] !== "string" && synthNames[i] !== "formant") continue;
      for (const slot of _slotState[i]) {
        if (!slot?.active || slot.nodeId == null) continue;
        socket.send(
          "/n_set",
          [
            { type: "i", value: slot.nodeId },
            { type: "s", value: parameter },
            { type: "i", value: ref.value ? 1 : 0 },
          ],
          OSC_LAYER_PORTS[i],
        );
      }
    }
  };

  // Routing changes release envelopes; only Panic deletes nodes and tails.
  const releaseAllNotes = () => {
    const now = performance.now();
    for (let i = 0; i < _slotState.length; i++) {
      for (const slot of _slotState[i]) {
        if (!slot.active || slot.nodeId == null) continue;
        const timestamp = slot.attackTimestamp != null && slot.attackTimestamp >= now
          ? slot.attackTimestamp + 1 : now;
        releaseNode(socket, OSC_LAYER_PORTS[i], slot.nodeId, 0, timestamp);
        slot.active = false;
        slot.nodeId = null;
        slot.token += 1;
      }
    }
    _knownNodeIds.clear();
    _pool.clear();
    _heldVoices.clear();
  };

  const freeAllKnownNodes = () => {
    for (const nodeId of _knownNodeIds) {
      for (const port of OSC_LAYER_PORTS) {
        socket.send("/n_free", [{ type: "i", value: nodeId }], port);
      }
    }
    for (const port of OSC_LAYER_PORTS) {
      freeTargetGroup(socket, port, targetGroup);
    }
    _knownNodeIds.clear();
    for (const layerState of _slotState) {
      for (const slot of layerState) {
        slot.active = false;
        slot.nodeId = null;
      }
    }
    _pool.clear();
    _heldVoices.clear();
  };

  return {
    family: "osc",
    // Existing voices keep their own reference; only new notes use this value.
    setTuningReference(value) {
      if (Number.isFinite(value) && value > 0) fundamental = value;
    },
    local: !!performanceOptions.transport,
    makeHex: (
      coords,
      cents,
      _steps,
      _equaves,
      _equivSteps,
      _cents_prev,
      _cents_next,
      note_played,
      velocity_played,
      bend,
      degree0toRef_ratio,
    ) => {
      if (shutdown) return silentOutputHex(coords, cents);
      return new OscHex(
        coords,
        cents,
        note_played,
        socket,
        synthNames,
        _volumes,
        targetGroup,
        fundamental,
        degree0toRef_ratio ?? 1,
        _mod,
        sendJitter,
        _quickRelease,
        _quickReleaseTime,
        _quickReleaseRasterOnly,
        _retriggerBuzzFormant,
        _pool,
        _slotState,
        _knownNodeIds,
        bend,
        velocity_played,
        _heldVoices,
      );
    },

    setMod(value) {
      _mod.value = value;
    },

    applyZoneModwheel(value) {
      const mod = midiCcToScParam(value);
      _mod.value = mod;
      sendJitter("modwheel");
      for (const port of OSC_LAYER_PORTS) {
        socket.send(
          "/n_set",
          [
            { type: "i", value: 1 },
            { type: "s", value: "expressionY" },
            { type: "f", value: mod - 1 },
          ],
          port,
        );
      }
    },

    rememberControllerState(state) {
      const mod = state?.ccValues?.[1];
      if (Number.isFinite(mod)) _mod.value = midiCcToScParam(mod);
    },

    applyControllerState(state) {
      const mod = state?.ccValues?.[1];
      if (Number.isFinite(mod)) _mod.value = midiCcToScParam(mod);
    },

    prepare() {
      return socket.prepare?.() ?? Promise.resolve();
    },

    ensureAwake() { return socket.prepare?.() ?? Promise.resolve(); },

    setVolume(_value) {
      // No-op for the OSC synth — each layer has its own volume controlled by
      // setLayerVolume. The master synth volume (use-synth-wiring.js setVolume
      // call after rebuild) must not overwrite the per-layer fader values.
    },

    setLayerVolume(index, value) {
      setLayerVolume(index, value);
    },

    setQuickRelease(value) {
      setQuickRelease(value);
    },

    setQuickReleaseTime(value) {
      setQuickReleaseTime(value);
    },

    setQuickReleaseRasterOnly(value) {
      setQuickReleaseRasterOnly(value);
    },

    setRetriggerBuzzFormant(value) {
      setBuzzFormantMode("retrigger_mode", _retriggerBuzzFormant, value);
    },

    allSoundOff() {
      debugLog("osc", "osc_synth.allSoundOff", { knownNodeCount: _knownNodeIds.size });
      socket.cancelScheduled?.();
      freeAllKnownNodes();
      socket.panic?.();
    },

    releaseAll() {
      debugLog("osc", "osc_synth.releaseAll", { knownNodeCount: _knownNodeIds.size });
      releaseAllNotes();
    },

    shutdown(options) {
      if (shutdown) return;
      shutdown = true;
      debugLog("osc", "osc_synth.shutdown", { knownNodeCount: _knownNodeIds.size });
      if (options?.panic) {
        socket.cancelScheduled?.();
        freeAllKnownNodes();
      } else releaseAllNotes();
      // Send scheduled attacks and their subsequent gate releases before closing.
      // Do not clear the shared socket's queue: another output may still own it.
      socket._flushBundles();
      socket.release({ graceful: !options?.panic });
    },

    getDiagnostics() {
      return {
        backend: performanceOptions.transport ? "supersonic" : "osc-bridge",
        synthLayers: [...synthNames],
        logicalActiveVoices: _pool.activeCount ?? null,
        activeNodesByLayer: Object.fromEntries(synthNames.map((name, i) => [
          name,
          _slotState[i].reduce((count, slot) => count + (slot.active ? 1 : 0), 0),
        ])),
        knownNodeIds: _knownNodeIds.size,
        bufferedSocketMessages: socket._queue?.length ?? 0,
        engine: performanceOptions.transport?.getDiagnostics?.() ?? null,
        disposed: shutdown,
      };
    },
  };
};

/**
 * OscHex — one instance per held note, one SC synth node per active layer.
 *
 * Voice slots are pooled like MTS real-time allocation, but SC node IDs are
 * always fresh so released tails cannot collide with a newly started note.
 */
function OscHex(
  coords,
  cents,
  notePlayed,
  socket,
  synthNames,
  volumes,
  targetGroup,
  fundamental,
  degree0toRef_ratio,
  modRef,
  sendJitter,
  quickReleaseRef,
  quickReleaseTimeRef,
  quickReleaseRasterOnlyRef,
  retriggerBuzzFormantRef,
  pool,
  slotState,
  knownNodeIds,
  bend,
  velocity_played,
  heldVoices,
) {
  this.coords = coords;
  this.cents = cents;
  this.release = false;

  this._socket = socket;
  this._synthNames = synthNames;
  this._volumes = volumes;
  this._targetGroup = targetGroup;
  this._fundamental = fundamental;
  this._degree0toRef = degree0toRef_ratio;
  this._modRef = modRef;
  this._sendJitter = sendJitter;
  this._quickReleaseRef = quickReleaseRef;
  this._quickReleaseTimeRef = quickReleaseTimeRef;
  this._quickReleaseRasterOnlyRef = quickReleaseRasterOnlyRef;
  this._retriggerBuzzFormantRef = retriggerBuzzFormantRef;
  this._pool = pool;
  this._heldVoices = heldVoices;
  this._slotState = slotState;
  this._knownNodeIds = knownNodeIds;
  this._slot = null;
  this._notePlayed = notePlayed;
  this._bend = Number.isFinite(bend) && bend > 0 ? bend : 1;
  this._onVel = clampMidiValue(velocity_played, 72);
  this._filter = 1;

  this._tokens = synthNames.map(() => null);
  this._nodeIds = synthNames.map((_, i) => nextNodeId(i));

  this._freq = this._centsToHz(cents);
}

OscHex.prototype.noteOn = function (timestamp) {
  if (this.release) return;
  if (!isPlayableScPitch(this._freq, this._bend)) {
    warnLog("[osc_synth] Ignoring out-of-range SuperCollider note", {
      coords: this.coords,
      cents: this.cents,
      frequency: this._freq,
      bend: this._bend,
      effectiveFrequency: this._freq * this._bend,
      supportedRangeHz: [SC_MIN_NOTE_HZ, SC_MAX_NOTE_HZ],
    });
    this.release = true;
    return;
  }
  const { slot } = this._pool.noteOn(this.coords, this._targetMidiFloat(), this);
  this._attackTimestamp = Number.isFinite(timestamp) ? timestamp : null;
  this._slot = slot;
  this._heldVoices.set(slot, this);
  this._nodeIds = this._synthNames.map((_, i) => nextNodeId(i));
  debugLog("osc", "OscHex.noteOn", {
    coords: this.coords,
    notePlayed: this._notePlayed,
    slot,
    nodeIds: this._nodeIds,
    freq: this._freq,
    onVel: this._onVel,
    bend: this._bend,
  });
  this._sendJitter?.("noteOn", this._notePlayed ?? this._slot ?? -1);

  for (let i = 0; i < this._synthNames.length; i++) {
    if (this._volumes[i] === 0) {
      this._nodeIds[i] = null;
      continue;
    }
    this._startLayer(i, timestamp);
  }
};

// Re-enable only this layer using the held voice's current pitch/expression.
// Never allocate a new logical slot or replay the other sounding layers.
OscHex.prototype._startLayer = function (i, timestamp) {
    if (this.release || this._slot == null || this._volumes[i] === 0 ||
        this._heldVoices.get(this._slot) !== this || !isPlayableScPitch(this._freq, this._bend)) return;
    this._nodeIds[i] = nextNodeId(i);
    const slotState = this._slotState[i][this._slot];
    if (slotState.active && slotState.nodeId != null) {
      releaseNode(this._socket, OSC_LAYER_PORTS[i], slotState.nodeId, slotState.onVel, timestamp);
    }
    slotState.token += 1;
    slotState.active = true;
    slotState.onVel = this._onVel;
    slotState.nodeId = this._nodeIds[i];
    slotState.attackTimestamp = Number.isFinite(timestamp) ? timestamp : null;
    slotState.quickReleaseEnabled = this._quickReleaseRasterOnlyRef.value
      ? this._rasterGenerated === true
      : true;
    this._knownNodeIds.add(this._nodeIds[i]);
    this._tokens[i] = slotState.token;

    this._socket.send(
      "/s_new",
      buildSNewArgs(
        this._synthNames[i],
        this._nodeIds[i],
        this._targetGroup,
        this._freq,
        this._bend,
        this._onVel,
        this._modRef.value,
        this._filter,
        this._volumes[i],
        slotState.quickReleaseEnabled ? this._quickReleaseRef.value : 0,
        this._quickReleaseTimeRef.value,
        this._retriggerBuzzFormantRef.value,
      ),
      OSC_LAYER_PORTS[i],
      timestamp,
    );
};

OscHex.prototype.needsRetainedVoiceRecovery = function () {
  // External bridges cannot confirm node lifetime. Never infer silence there.
  if (!this._socket.hasNode) return false;
  return this._volumes.some((volume, i) => volume > 0 &&
    !this._socket.hasNode(this._nodeIds[i]));
};

OscHex.prototype.recoverRetainedVoice = function (_note, timestamp) {
  if (this.release || !this.needsRetainedVoiceRecovery()) return false;
  if (this._heldVoices.get(this._slot) !== this) {
    this.noteOn(timestamp);
  } else {
    for (let i = 0; i < this._volumes.length; i++) {
      if (this._volumes[i] === 0 || this._socket.hasNode(this._nodeIds[i])) continue;
      // The server already ended this node: do not send a gate to a stale id.
      const state = this._slotState[i][this._slot];
      state.active = false;
      state.nodeId = null;
      this._startLayer(i, timestamp);
    }
  }
  return true;
};

OscHex.prototype.noteOff = function (release_velocity, timestamp) {
  if (this.release) return;
  this.release = true;
  // Output activation may schedule the attack ahead of a physical key release.
  // Give the server a strictly later gate event, even across separate bundles.
  const requested = Number.isFinite(timestamp) ? timestamp : performance.now();
  if (this._attackTimestamp != null && requested <= this._attackTimestamp) {
    timestamp = this._attackTimestamp + 1;
  }
  const resolvedSlot = this._pool.noteOff(this.coords, this);
  const slot = resolvedSlot ?? this._slot;
  if (this._heldVoices.get(slot) === this) this._heldVoices.delete(slot);
  debugLog("osc", "OscHex.noteOff", {
    coords: this.coords,
    notePlayed: this._notePlayed,
    requestedSlot: this._slot,
    resolvedSlot,
    releaseSlot: slot,
    nodeIds: this._nodeIds,
    releaseVelocity: release_velocity,
  });
  this._sendJitter?.("noteOff", this._notePlayed ?? slot ?? -1);
  if (slot == null) return;
  for (let i = 0; i < this._synthNames.length; i++) {
    const slotState = this._slotState[i][slot];
    if (!slotState.active || slotState.token !== this._tokens[i]) continue;
    releaseNode(
      this._socket,
      OSC_LAYER_PORTS[i],
      this._nodeIds[i],
      normalizeOffVelocity(release_velocity, slotState.onVel),
      timestamp,
    );
    slotState.active = false;
    slotState.nodeId = null;
    slotState.quickReleaseEnabled = false;
    this._knownNodeIds.delete(this._nodeIds[i]);
  }
};

OscHex.prototype.forceFree = function () {
  if (this.release) return;
  this.release = true;
  const resolvedSlot = this._pool.noteOff(this.coords, this);
  const slot = resolvedSlot ?? this._slot;
  if (this._heldVoices.get(slot) === this) this._heldVoices.delete(slot);
  debugLog("osc", "OscHex.forceFree", {
    coords: this.coords,
    requestedSlot: this._slot,
    resolvedSlot,
    releaseSlot: slot,
    nodeIds: this._nodeIds,
  });
  for (let i = 0; i < this._synthNames.length; i++) {
    const nodeId = this._nodeIds[i];
    if (nodeId == null) continue;
    const timestamp = this._attackTimestamp != null && performance.now() <= this._attackTimestamp
      ? this._attackTimestamp + 1 : undefined;
    this._socket.send("/n_free", [{ type: "i", value: nodeId }], OSC_LAYER_PORTS[i], timestamp);
    this._knownNodeIds.delete(nodeId);
    if (slot != null) {
      const slotState = this._slotState[i][slot];
      slotState.active = false;
      slotState.nodeId = null;
      slotState.quickReleaseEnabled = false;
    }
  }
};

OscHex.prototype.retune = function (newCents) {
  if (this.release) return;
  this.cents = newCents;
  this._freq = this._centsToHz(newCents);
  if (!isPlayableScPitch(this._freq, this._bend)) {
    warnLog("[osc_synth] Ignoring out-of-range SuperCollider retune", {
      coords: this.coords,
      cents: this.cents,
      frequency: this._freq,
      bend: this._bend,
      effectiveFrequency: this._freq * this._bend,
      supportedRangeHz: [SC_MIN_NOTE_HZ, SC_MAX_NOTE_HZ],
    });
    return;
  }
  this._sendJitter?.("retune", this._notePlayed ?? this._slot ?? -1);
  for (let i = 0; i < this._synthNames.length; i++) {
    const nodeId = this._nodeIds[i];
    if (nodeId == null) continue;
    this._socket.send(
      "/n_set",
      [
        { type: "i", value: nodeId },
        { type: "s", value: "freq" },
        { type: "f", value: this._freq },
      ],
      OSC_LAYER_PORTS[i],
      outputTimestamp(),
    );
  }
};

OscHex.prototype.sequenceRetune = function (newCents) {
  this.retune(newCents);
};

OscHex.prototype.aftertouch = function (value, value14 = null) {
  if (this.release) return;
  const filter =
    1 + (Number.isFinite(value14) ? Math.max(0, Math.min(16256, value14)) / 16256 : value / 127);
  this._filter = filter;
  this._sendJitter?.("aftertouch", this._notePlayed ?? this._slot ?? -1);
  for (let i = 0; i < this._synthNames.length; i++) {
    if (this._slot == null) continue;
    const slotState = this._slotState[i][this._slot];
    if (!slotState.active || slotState.token !== this._tokens[i]) continue;
    this._socket.send(
      "/n_set",
      [
        { type: "i", value: this._nodeIds[i] },
        { type: "s", value: "pressure" },
        { type: "f", value: filter - 1 },
      ],
      OSC_LAYER_PORTS[i],
    );
  }
};

const snapshotPressureKey = (value, value14) =>
  `${Number(value) || 0}:${Number.isFinite(value14) ? Number(value14) : ""}`;

OscHex.prototype.prepareSnapshotPressure = function (value, value14 = null) {
  if (this.release) return;
  this._filter =
    1 + (Number.isFinite(value14) ? Math.max(0, Math.min(16256, value14)) / 16256 : value / 127);
  this._preparedSnapshotPressure = snapshotPressureKey(value, value14);
};

OscHex.prototype.applySnapshotPressure = function (value, value14 = null) {
  const key = snapshotPressureKey(value, value14);
  if (this._preparedSnapshotPressure === key) {
    this._preparedSnapshotPressure = null;
    return;
  }
  this.aftertouch(value, value14);
};

// Floating-point snapshot expression goes directly to OSC. These scaled
// arguments deliberately stay fractional: no MIDI quantization is involved.
OscHex.prototype.prepareNormalizedSnapshotPressure = function (value) {
  this._filter = 1 + Math.max(0, Math.min(1, value));
};
OscHex.prototype.applyNormalizedSnapshotPressure = function (value) {
  this.aftertouch(value * 127, value * 16256);
};
OscHex.prototype.applyNormalizedSnapshotTimbre = function (value) {
  this.cc74(value * 127, value * 16256);
};

OscHex.prototype.pressure = function (value, value14 = null) {
  this.aftertouch(value, value14);
};

OscHex.prototype.pitchbend = function (value) {
  if (this.release) return;
  if (!isPlayableScPitch(this._freq, value)) {
    warnLog("[osc_synth] Ignoring out-of-range SuperCollider pitch bend", {
      coords: this.coords,
      frequency: this._freq,
      bend: value,
      effectiveFrequency: this._freq * value,
      supportedRangeHz: [SC_MIN_NOTE_HZ, SC_MAX_NOTE_HZ],
    });
    return;
  }
  this._bend = value;
  this._sendJitter?.("pitchbend", this._notePlayed ?? this._slot ?? -1);
  for (let i = 0; i < this._synthNames.length; i++) {
    if (this._slot == null) continue;
    const slotState = this._slotState[i][this._slot];
    if (!slotState.active || slotState.token !== this._tokens[i]) continue;
    this._socket.send(
      "/n_set",
      [
        { type: "i", value: this._nodeIds[i] },
        { type: "s", value: "bend" },
        { type: "f", value },
      ],
      OSC_LAYER_PORTS[i],
    );
  }
};

// CC74 / Y expression → expressionY on individual nodes
OscHex.prototype.cc74 = function (value, value14 = null) {
  if (this.release) return;
  const mod =
    1 + (Number.isFinite(value14) ? Math.max(0, Math.min(16256, value14)) / 16256 : value / 127);
  this._modRef.value = mod;
  this._sendJitter?.("cc74", this._notePlayed ?? this._slot ?? -1);
  for (let i = 0; i < this._synthNames.length; i++) {
    if (this._slot == null) continue;
    const slotState = this._slotState[i][this._slot];
    if (!slotState.active || slotState.token !== this._tokens[i]) continue;
    this._socket.send(
      "/n_set",
      [
        { type: "i", value: this._nodeIds[i] },
        { type: "s", value: "expressionY" },
        { type: "f", value: mod - 1 },
      ],
      OSC_LAYER_PORTS[i],
    );
  }
};

// Modwheel → broadcast /n_set expressionY to node 1 on all four servers
OscHex.prototype.modwheel = function (value) {
  const mod = midiCcToScParam(value);
  this._modRef.value = mod;
  this._sendJitter?.("modwheel");
  for (const port of OSC_LAYER_PORTS) {
    this._socket.send(
      "/n_set",
      [
        { type: "i", value: 1 },
        { type: "s", value: "expressionY" },
        { type: "f", value: mod - 1 },
      ],
      port,
    );
  }
};

OscHex.prototype.expression = function () {};

/**
 * Convert scale-relative cents to Hz.
 *   ref        = fundamental / degree0toRef_ratio   (Hz of scale degree 0)
 *   ref_offset = 1200 * log2(ref / C4_440)
 *   Hz         = C4_440 * 2^((cents + ref_offset) / 1200)
 */
OscHex.prototype._centsToHz = function (cents) {
  const C4_440 = 261.6255653;
  const ref = this._fundamental / this._degree0toRef;
  const ref_offset = 1200 * Math.log2(ref / C4_440);
  return C4_440 * Math.pow(2, (cents + ref_offset) / 1200);
};

OscHex.prototype._targetMidiFloat = function () {
  const ref = this._fundamental / this._degree0toRef;
  const refOffset = 1200 * Math.log2(ref / 261.6255653);
  const refCents = this.cents + refOffset;
  return refCents * 0.01 + 60;
};
